import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { 
  BookOpen, 
  Users, 
  Calendar, 
  BarChart3, 
  LogOut,
  Plus,
  Edit,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Save,
  Timer,
  UserCircle,
  CalendarDays,
  Trash2,
  Shield,
  Menu,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ClockInOutTab from './ClockInOutTab';
import CalendarsTab from './CalendarsTab';
import MyProfileTab from './MyProfileTab';
import WeeklyPlanningTab from './WeeklyPlanningTab';
import LeaveRequestsTab from './LeaveRequestsTab';

// Helper function to handle validation errors
const handleValidationError = (error) => {
  if (error.response?.data?.detail) {
    if (Array.isArray(error.response.data.detail)) {
      // Handle Pydantic validation errors
      const messages = error.response.data.detail.map(err => `${err.loc?.join('.')} - ${err.msg}`).join('\n');
      return messages;
    } else {
      return error.response.data.detail;
    }
  }
  return 'Error desconocido';
};

// Local date helpers (avoid UTC off-by-one in Puerto Rico / UTC-4).
// Returns today's date as 'YYYY-MM-DD' using LOCAL time (not UTC).
const todayLocalStr = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

// Formats a 'YYYY-MM-DD' string for display WITHOUT timezone shift.
const formatLocalDate = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = String(dateStr).split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  return new Date(y, m - 1, d).toLocaleDateString();
};

const TeacherDashboard = () => {
  const { logout, user, schoolInfo } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({});
  const [teacherProfile, setTeacherProfile] = useState(null);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedDate, setSelectedDate] = useState(todayLocalStr());
  const [showGradeDialog, setShowGradeDialog] = useState(false);
  const [showAttendanceDialog, setShowAttendanceDialog] = useState(false);
  const [gradingSystem, setGradingSystem] = useState('points'); // Global grading system
  const [selectedPeriod, setSelectedPeriod] = useState('10'); // 10/20/30/40 semanas
  const [allStudents, setAllStudents] = useState([]); // all teacher students (for overview counts)
  const [studentInfo, setStudentInfo] = useState(null); // student detail modal
  const [studentAttendance, setStudentAttendance] = useState(null);

  const PERIODS = [
    { value: '10', label: '10 semanas' },
    { value: '20', label: '20 semanas' },
    { value: '30', label: '30 semanas' },
    { value: '40', label: '40 semanas' },
  ];

  // Compute average + letter for a list of grade records (used for period and global).
  const computeGradeSummary = (gradeList) => {
    const valid = (gradeList || []).filter(g => g && g.grade_value !== null && g.grade_value !== undefined && g.grade_value !== '');
    if (valid.length === 0) return { average: gradingSystem === 'letter' ? '-' : (gradingSystem === 'points' ? '0.00' : '0'), displayGrade: '-' };
    if (gradingSystem === 'custom_points') {
      let totalScore = 0, totalMax = 0;
      valid.forEach(g => { totalScore += g.grade_value; totalMax += (g.max_points || 0); });
      const pct = Math.round(totalMax > 0 ? (totalScore / totalMax) * 100 : 0);
      return { average: pct + '%', displayGrade: pct >= 90 ? 'A' : pct >= 80 ? 'B' : pct >= 70 ? 'C' : pct >= 60 ? 'D' : 'F' };
    }
    const sum = valid.reduce((a, b) => a + b.grade_value, 0);
    const avg = sum / valid.length;
    if (gradingSystem === 'points') {
      return { average: avg.toFixed(2), displayGrade: avg >= 3.5 ? 'A' : avg >= 3 ? 'B' : avg >= 2.5 ? 'C' : avg >= 2 ? 'D' : 'F' };
    }
    if (gradingSystem === 'percentage') {
      const rounded = Math.round(avg);
      return { average: rounded + '%', displayGrade: rounded >= 90 ? 'A' : rounded >= 80 ? 'B' : rounded >= 70 ? 'C' : rounded >= 60 ? 'D' : 'F' };
    }
    // letter
    const dg = avg >= 3.5 ? 'A' : avg >= 3 ? 'B' : avg >= 2.5 ? 'C' : avg >= 2 ? 'D' : 'F';
    return { average: dg, displayGrade: dg };
  };
  const [evaluationDescriptions, setEvaluationDescriptions] = useState(
    Array(15).fill('').map((_, i) => `Evaluación ${i + 1}`)
  );
  const [evaluationDates, setEvaluationDates] = useState(
    Array(15).fill('')
  );

  // Grade form state
  const [gradeForm, setGradeForm] = useState({
    student_id: '',
    subject: '',
    grade_value: '',
    grade_system: 'points',
    max_points: '',
    description: '',
    notes: '',
    period: '10',
    evaluation_date: '',
    grade_id: null // Para saber si estamos editando
  });

  // Attendance form state
  const [attendanceForm, setAttendanceForm] = useState({
    student_id: '',
    grade_class: '',
    date: todayLocalStr(),
    status: 'present',
    notes: ''
  });
  const [bulkAttendance, setBulkAttendance] = useState({});
  const [savingBulk, setSavingBulk] = useState(false);

  useEffect(() => {
    fetchTeacherStats();
    fetchTeacherProfile();
    fetchSubjects();
    fetchGradingSystem();
    fetchAllStudents();
  }, []);

  useEffect(() => {
    if (selectedSubject) {
      fetchStudentsBySubject(selectedSubject);
      fetchGradesBySubject();
      fetchEvaluationDescriptions(selectedSubject, selectedPeriod);
    }
  }, [selectedSubject]);

  useEffect(() => {
    if (selectedSubject) {
      fetchEvaluationDescriptions(selectedSubject, selectedPeriod);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    if (selectedSubject && selectedDate && students.length > 0) {
      fetchAttendanceByDate();
    }
  }, [selectedSubject, selectedDate, students]);

  const fetchTeacherStats = async () => {
    try {
      const response = await axios.get('/dashboard/teacher-stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching teacher stats:', error);
    }
  };

  const fetchTeacherProfile = async () => {
    try {
      const response = await axios.get('/teachers/profile');
      setTeacherProfile(response.data);
      // Set first subject as default if available
      if (response.data?.subjects?.length > 0) {
        setSelectedSubject(response.data.subjects[0]);
      }
    } catch (error) {
      console.error('Error fetching teacher profile:', error);
      // Set default teacher profile if none exists
      setTeacherProfile({
        full_name: user?.username || 'Profesor',
        teacher_number: 'N/A',
        assigned_grades: [],
        subjects: []
      });
      toast.error('Perfil de profesor no encontrado. Contacte al administrador.');
    }
  };

  const fetchGradingSystem = async () => {
    try {
      const response = await axios.get('/settings/grading-system');
      const system = response.data.grading_system || 'points';
      setGradingSystem(system);
      setGradeForm(prev => ({ ...prev, grade_system: system }));
    } catch (error) {
      console.error('Error fetching grading system:', error);
      // Default to points if error
      setGradingSystem('points');
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get('/subjects');
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const fetchAllStudents = async () => {
    try {
      const profileRes = await axios.get('/teachers/profile');
      const assignedGrades = profileRes.data?.assigned_grades || [];
      const res = await axios.get('/students');
      const mine = res.data.filter(s => s.is_active && (assignedGrades.length === 0 || assignedGrades.includes(s.assigned_grade)));
      setAllStudents(mine);
    } catch (error) {
      console.error('Error fetching all students:', error);
    }
  };

  const openStudentInfo = async (student) => {
    setStudentInfo(student);
    setStudentAttendance(null);
    try {
      const res = await axios.get(`/attendance/student/${student.id}`);
      const recs = res.data || [];
      const summary = { present: 0, late: 0, absent: 0, excused: 0, total: recs.length };
      recs.forEach(r => { if (summary[r.status] !== undefined) summary[r.status]++; });
      setStudentAttendance(summary);
    } catch (error) {
      setStudentAttendance({ present: 0, late: 0, absent: 0, excused: 0, total: 0 });
    }
  };

  const fetchStudentsBySubject = async (subjectName) => {
    try {
      // First, get the subject details to find which grades it's assigned to
      const subjectsResponse = await axios.get('/subjects');
      const subject = subjectsResponse.data.find(s => s.name === subjectName);
      
      if (!subject || !subject.assigned_grades || subject.assigned_grades.length === 0) {
        toast.info('Esta materia no tiene grados asignados');
        setStudents([]);
        return;
      }
      
      // Get all students from all grades where this subject is taught
      const allStudentsResponse = await axios.get('/students');
      const filteredStudents = allStudentsResponse.data.filter(student => 
        subject.assigned_grades.includes(student.assigned_grade) && student.is_active
      );
      
      setStudents(filteredStudents);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Error al cargar estudiantes');
    }
  };

  const fetchAttendanceByDate = async () => {
    if (!selectedSubject || !selectedDate || students.length === 0) return;
    
    try {
      // Get unique grades from the students list
      const grades = [...new Set(students.map(s => s.assigned_grade))];
      
      // Fetch attendance for each grade
      const allAttendance = [];
      for (const grade of grades) {
        try {
          const response = await axios.get(`/attendance/class/${grade}?date=${selectedDate}`);
          if (response.data?.attendance) {
            allAttendance.push(...response.data.attendance);
          }
        } catch (err) {
          console.error(`Error fetching attendance for grade ${grade}:`, err);
        }
      }
      
      // Filter to only include students from our list
      const studentIds = students.map(s => s.id);
      const filtered = allAttendance.filter(att => studentIds.includes(att.student_id));
      setAttendanceRecords(filtered || []);

      // Seed the bulk selection: existing status, else default 'present'
      const seed = {};
      students.forEach(s => {
        const rec = filtered.find(a => a.student_id === s.id);
        seed[s.id] = rec ? rec.status : 'present';
      });
      setBulkAttendance(seed);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  const handleBulkAttendanceSave = async () => {
    if (!selectedDate || students.length === 0) return;
    setSavingBulk(true);
    try {
      const records = students.map(s => ({
        student_id: s.id,
        grade_class: s.assigned_grade,
        status: bulkAttendance[s.id] || 'present',
      }));
      const res = await axios.post('/attendance/bulk', { date: selectedDate, records });
      toast.success(res.data.message || 'Asistencia guardada');
      fetchAttendanceByDate();
      fetchTeacherStats();
    } catch (error) {
      console.error('Error saving bulk attendance:', error);
      toast.error(handleValidationError(error));
    } finally {
      setSavingBulk(false);
    }
  };

  const setAllAttendance = (status) => {
    const next = {};
    students.forEach(s => { next[s.id] = status; });
    setBulkAttendance(next);
  };

  const fetchGradesBySubject = async () => {
    if (!selectedSubject) return;
    
    try {
      const response = await axios.get(`/grades?subject=${selectedSubject}`);
      setGrades(response.data || []);
    } catch (error) {
      console.error('Error fetching grades:', error);
    }
  };

  const fetchEvaluationDescriptions = async (subject, period = '10') => {
    try {
      const response = await axios.get(`/evaluation-descriptions/${encodeURIComponent(subject)}?period=${period}`);
      const descriptions = response.data?.descriptions;
      const dates = response.data?.dates;
      if (descriptions && descriptions.length > 0) {
        setEvaluationDescriptions(descriptions);
        setEvaluationDates(dates && dates.length > 0 ? dates.concat(Array(Math.max(0, descriptions.length - dates.length)).fill('')) : Array(descriptions.length).fill(''));
      } else {
        setEvaluationDescriptions(Array(15).fill('').map((_, i) => `Evaluación ${i + 1}`));
        setEvaluationDates(Array(15).fill(''));
      }
    } catch (error) {
      console.error('Error fetching evaluation descriptions:', error);
      setEvaluationDescriptions(Array(15).fill('').map((_, i) => `Evaluación ${i + 1}`));
      setEvaluationDates(Array(15).fill(''));
    }
  };

  const addEvaluationColumn = () => {
    setEvaluationDescriptions(prev => [...prev, `Evaluación ${prev.length + 1}`]);
    setEvaluationDates(prev => [...prev, '']);
  };

  const removeEvaluationColumn = (index) => {
    setEvaluationDescriptions(prev => prev.filter((_, i) => i !== index));
    setEvaluationDates(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveDescriptions = async () => {
    if (!selectedSubject) {
      toast.error('Selecciona una materia primero');
      return;
    }
    try {
      setLoading(true);
      await axios.put('/evaluation-descriptions', {
        subject: selectedSubject,
        period: selectedPeriod,
        descriptions: evaluationDescriptions,
        dates: evaluationDates
      });
      toast.success('Descripciones guardadas exitosamente');
    } catch (error) {
      console.error('Error saving descriptions:', error);
      toast.error('Error al guardar descripciones');
    } finally {
      setLoading(false);
    }
  };

  const handleGradeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...gradeForm,
        grade_value: parseFloat(gradeForm.grade_value),
        max_points: gradeForm.max_points ? parseFloat(gradeForm.max_points) : null,
        period: gradeForm.period || selectedPeriod,
        evaluation_date: gradeForm.evaluation_date || null
      };

      // Si existe grade_id, buscar y actualizar la calificación existente
      if (gradeForm.grade_id) {
        const existingGrade = grades.find(g => g.id === gradeForm.grade_id);
        if (existingGrade) {
          await axios.delete(`/grades/${gradeForm.grade_id}`);
        }
      } else {
        // Evitar duplicado para mismo estudiante, materia, descripción Y período
        const existingGrade = grades.find(g =>
          g.student_id === gradeForm.student_id &&
          g.subject === gradeForm.subject &&
          g.description === gradeForm.description &&
          (g.period || '10') === (gradeForm.period || selectedPeriod)
        );
        if (existingGrade) {
          await axios.delete(`/grades/${existingGrade.id}`);
        }
      }

      // Crear la nueva calificación
      await axios.post('/grades', submitData);
      
      toast.success('Calificación guardada exitosamente');
      setShowGradeDialog(false);
      setGradeForm({
        student_id: '',
        subject: '',
        grade_value: '',
        grade_system: gradingSystem,
        max_points: '',
        description: '',
        notes: '',
        period: selectedPeriod,
        evaluation_date: '',
        grade_id: null
      });
      fetchGradesBySubject();
      fetchTeacherStats();
    } catch (error) {
      console.error('Error creating grade:', error);
      toast.error(handleValidationError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGrade = async () => {
    if (!gradeForm.grade_id) return;
    if (!window.confirm('¿Eliminar esta calificación? Esta acción no se puede deshacer.')) return;
    try {
      setLoading(true);
      await axios.delete(`/grades/${gradeForm.grade_id}`);
      toast.success('Calificación eliminada');
      setShowGradeDialog(false);
      setGradeForm({
        student_id: '',
        subject: '',
        grade_value: '',
        grade_system: gradingSystem,
        max_points: '',
        description: '',
        notes: '',
        period: selectedPeriod,
        evaluation_date: '',
        grade_id: null
      });
      fetchGradesBySubject();
      fetchTeacherStats();
    } catch (error) {
      console.error('Error deleting grade:', error);
      toast.error(handleValidationError(error));
    } finally {
      setLoading(false);
    }
  };


  const handleAttendanceSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...attendanceForm,
        date: new Date(attendanceForm.date).toISOString()
      };

      // Check if we're editing an existing attendance record
      // Get all records for this student on this date
      const existingRecords = attendanceRecords.filter(
        a => a.student_id === attendanceForm.student_id && 
        new Date(a.date).toDateString() === new Date(attendanceForm.date).toDateString()
      );

      if (existingRecords.length > 0) {
        // Update all existing records (in case there are duplicates)
        // We'll update the most recent one and delete the rest
        const sortedRecords = existingRecords.sort((a, b) => 
          new Date(b.created_at || b.date) - new Date(a.created_at || a.date)
        );
        
        const recordToUpdate = sortedRecords[0];
        
        // Update the most recent record
        await axios.put(`/attendance/${recordToUpdate.id}`, {
          status: attendanceForm.status,
          notes: attendanceForm.notes
        });
        
        // Delete duplicate records if any
        for (let i = 1; i < sortedRecords.length; i++) {
          try {
            await axios.delete(`/attendance/${sortedRecords[i].id}`);
          } catch (err) {
            console.error('Error deleting duplicate:', err);
          }
        }
        
        toast.success('Asistencia actualizada exitosamente');
      } else {
        // Create new record
        await axios.post('/attendance', submitData);
        toast.success('Asistencia registrada exitosamente');
      }
      
      setShowAttendanceDialog(false);
      setAttendanceForm({
        student_id: '',
        grade_class: '',
        date: todayLocalStr(),
        status: 'present',
        notes: ''
      });
      fetchAttendanceByDate();
      fetchTeacherStats();
    } catch (error) {
      console.error('Error saving attendance:', error);
      toast.error(handleValidationError(error));
    } finally {
      setLoading(false);
    }
  };

  const getAttendanceStatus = (studentId) => {
    const record = attendanceRecords.find(a => a.student_id === studentId);
    return record?.status || 'not_marked';
  };

  const getAttendanceIcon = (status) => {
    switch (status) {
      case 'present':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'absent':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'late':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'excused':
        return <Shield className="w-5 h-5 text-blue-600" />;
      default:
        return <div className="w-5 h-5 rounded-full border-2 border-gray-300" />;
    }
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <Card className="modern-card hover:shadow-lg transition-all duration-200">
      <CardContent className="flex items-center p-6">
        <div className={`p-3 rounded-xl ${color} mr-4`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className="text-2xl font-bold text-slate-800">{value}</p>
        </div>
      </CardContent>
    </Card>
  );

  if (!teacherProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="teacher-sidebar-overlay"
        />
      )}
      {/* Sidebar */}
      <div className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-content">
          <div className="p-5 pb-3">
            <div className="flex items-center mb-5">
              <div className="w-14 h-14 rounded-xl mr-3 flex items-center justify-center shrink-0"
                style={schoolInfo?.school_logo_url
                  ? { background: '#ffffff', padding: '3px' }
                  : { background: 'linear-gradient(135deg, #f97316, #ea580c)' }}>
                {schoolInfo?.school_logo_url ? (
                  <img src={schoolInfo.school_logo_url.startsWith('data:') ? schoolInfo.school_logo_url : `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}${schoolInfo.school_logo_url}`} alt="" className="w-full h-full object-contain rounded-lg" />
                ) : (
                  <BookOpen className="w-7 h-7 text-white" />
                )}
              </div>
              <div>
                <h1 className="text-base font-bold text-white tracking-tight">
                  {schoolInfo?.school_name || (
                    <span>
                      EduCore
                      <span className="relative inline-block">
                        <span>®</span>
                        <span className="absolute left-full bottom-1/2 text-[6px] font-bold tracking-[0.1em] text-slate-400 leading-none ml-0.5">BRAND</span>
                      </span>
                    </span>
                  )}
                </h1>
                <p className="text-[11px] text-slate-400 font-medium">Profesor</p>
              </div>
            </div>
            
            <Button 
              onClick={logout}
              variant="outline" 
              size="sm"
              className="w-full bg-white/5 border-white/10 text-slate-300 hover:bg-red-500/20 hover:text-red-300 hover:border-red-400/30 transition-all text-xs mb-2"
            >
              <LogOut className="w-3 h-3 mr-1" />
              Cerrar Sesión
            </Button>
          </div>

          <div className="sidebar-nav">
            <nav className="space-y-2">
              <button
                onClick={() => { setActiveTab('overview'); setSidebarOpen(false); }}
                className={`sidebar-nav-item w-full text-left ${activeTab === 'overview' ? 'active' : ''}`}
              >
                <BarChart3 className="w-5 h-5 mr-3" />
                Dashboard
              </button>
              <button
                onClick={() => { setActiveTab('students'); setSidebarOpen(false); }}
                className={`sidebar-nav-item w-full text-left ${activeTab === 'students' ? 'active' : ''}`}
              >
                <Users className="w-5 h-5 mr-3" />
                Mis Estudiantes
              </button>
              <button
                onClick={() => { setActiveTab('grades'); setSidebarOpen(false); }}
                className={`sidebar-nav-item w-full text-left ${activeTab === 'grades' ? 'active' : ''}`}
              >
                <BookOpen className="w-5 h-5 mr-3" />
                Calificaciones
              </button>
              <button
                onClick={() => { setActiveTab('attendance'); setSidebarOpen(false); }}
                className={`sidebar-nav-item w-full text-left ${activeTab === 'attendance' ? 'active' : ''}`}
              >
                <Calendar className="w-5 h-5 mr-3" />
                Asistencia
              </button>
              <button
                onClick={() => { setActiveTab('planning'); setSidebarOpen(false); }}
                className={`sidebar-nav-item w-full text-left ${activeTab === 'planning' ? 'active' : ''}`}
                data-testid="teacher-nav-planning"
              >
                <BookOpen className="w-5 h-5 mr-3" />
                Planificación
              </button>
              <button
                onClick={() => { setActiveTab('calendars'); setSidebarOpen(false); }}
                className={`sidebar-nav-item w-full text-left ${activeTab === 'calendars' ? 'active' : ''}`}
                data-testid="teacher-nav-calendars"
              >
                <Calendar className="w-5 h-5 mr-3" />
                Calendarios
              </button>
              <div className="pt-3 pb-1 px-3">
                <p className="text-xs text-white/40 uppercase tracking-wider">Personal</p>
              </div>
              <button
                onClick={() => { setActiveTab('clockinout'); setSidebarOpen(false); }}
                className={`sidebar-nav-item w-full text-left ${activeTab === 'clockinout' ? 'active' : ''}`}
                data-testid="teacher-nav-clockinout"
              >
                <Timer className="w-5 h-5 mr-3" />
                Reloj de Ponche
              </button>
              <button
                onClick={() => { setActiveTab('myprofile'); setSidebarOpen(false); }}
                className={`sidebar-nav-item w-full text-left ${activeTab === 'myprofile' ? 'active' : ''}`}
                data-testid="teacher-nav-myprofile"
              >
                <UserCircle className="w-5 h-5 mr-3" />
                Mi Perfil
              </button>
              <button
                onClick={() => { setActiveTab('leave-requests'); setSidebarOpen(false); }}
                className={`sidebar-nav-item w-full text-left ${activeTab === 'leave-requests' ? 'active' : ''}`}
                data-testid="teacher-nav-leave-requests"
              >
                <CalendarDays className="w-5 h-5 mr-3" />
                Mis Licencias
              </button>
            </nav>
          </div>

          <div className="sidebar-footer">
            <div className="bg-white/5 rounded-xl p-3 border border-white/5">
              <p className="text-xs text-white font-medium">{teacherProfile?.full_name || user?.username || 'Profesor'}</p>
              <p className="text-[11px] text-slate-400">Profesor</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-main">
        <div className="mb-8">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden fixed top-4 left-4 z-40 bg-slate-800 hover:bg-slate-700 text-white p-2 rounded-lg shadow-lg"
            data-testid="teacher-menu-toggle"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex items-center gap-3 mb-1 lg:ml-0 ml-14">
            <div className="w-1 h-8 bg-gradient-to-b from-orange-400 to-orange-600 rounded-full"></div>
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
              Panel del Profesor
            </h1>
          </div>
          <p className="text-sm text-slate-500 ml-5 lg:ml-5">
            Gestiona calificaciones y asistencia de tus estudiantes
          </p>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Total Estudiantes"
                value={stats.total_students || 0}
                icon={Users}
                color="bg-gradient-to-br from-blue-500 to-blue-600"
              />
              <StatCard
                title="Calificaciones Dadas"
                value={stats.total_grades || 0}
                icon={BookOpen}
                color="bg-gradient-to-br from-blue-500 to-blue-600"
              />
              <StatCard
                title="Registros de Asistencia"
                value={stats.total_attendance || 0}
                icon={Calendar}
                color="bg-gradient-to-br from-orange-500 to-orange-600"
              />
              <StatCard
                title="Grados Asignados"
                value={stats.assigned_grades?.length || 0}
                icon={BarChart3}
                color="bg-gradient-to-br from-purple-500 to-purple-600"
              />
            </div>

            {/* Profile Card */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="modern-card">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <BookOpen className="w-5 h-5 mr-2 text-blue-600" />
                    Mi Perfil
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500">Nombre Completo</p>
                    <p className="font-medium">{teacherProfile?.full_name || user?.username || 'No disponible'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Número de Profesor</p>
                    <p className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{teacherProfile?.teacher_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Grados Asignados</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {teacherProfile?.assigned_grades?.map((grade) => (
                        <Badge key={grade} variant="secondary">{grade}</Badge>
                      )) || <span className="text-slate-400">No asignados</span>}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Materias</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {teacherProfile?.subjects?.map((subject) => (
                        <Badge key={subject} variant="outline">{subject}</Badge>
                      )) || <span className="text-slate-400">No asignadas</span>}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="modern-card">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Users className="w-5 h-5 mr-2 text-blue-600" />
                    Grados y Estudiantes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {teacherProfile?.assigned_grades?.length > 0 ? 
                      teacherProfile?.assigned_grades?.map((grade) => (
                        <div key={grade} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{grade}</p>
                              <p className="text-sm text-slate-500">
                                {allStudents.filter(s => s.assigned_grade === grade).length} estudiantes
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setActiveTab('students');
                              }}
                            >
                              Ver
                            </Button>
                          </div>
                        </div>
                      )) : (
                        <p className="text-slate-400 text-center py-4">No tienes grados asignados</p>
                      )
                    }
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Mis Estudiantes</h2>
                <p className="text-slate-500">Estudiantes que toman tus materias</p>
              </div>
              <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Seleccionar Materia" />
                </SelectTrigger>
                <SelectContent>
                  {teacherProfile?.subjects && teacherProfile.subjects.length > 0 ? (
                    teacherProfile.subjects.filter(subject => subject && subject.trim()).map((subject) => (
                      <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-subjects">No hay materias asignadas</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {selectedSubject && (
              <Card className="modern-card">
                <CardHeader>
                  <CardTitle>Estudiantes de {selectedSubject}</CardTitle>
                  <CardDescription>
                    {students.length} estudiantes matriculados
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Nombre Completo</th>
                          <th>Email</th>
                          <th>Teléfono</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.map((student) => (
                          <tr key={student.id}>
                            <td>
                              <div>
                                <p className="font-medium text-slate-800">
                                  {student.first_name} {student.middle_name || ''} {student.last_name_paternal} {student.last_name_maternal || ''}
                                </p>
                                {student.student_number && (
                                  <p className="text-sm text-slate-400">{student.student_number}</p>
                                )}
                              </div>
                            </td>
                            <td className="text-sm">{student.email}</td>
                            <td className="text-sm">{student.phone_number}</td>
                            <td>
                              <div className="flex space-x-2">
                                <Button size="sm" variant="outline" onClick={() => openStudentInfo(student)} data-testid={`view-student-${student.id}`}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    setGradeForm({...gradeForm, student_id: student.id});
                                    setShowGradeDialog(true);
                                  }}
                                >
                                  Calificar
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Grades Tab */}
        {activeTab === 'grades' && (
          <div className="space-y-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-center">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Registro de Calificaciones</h2>
                <p className="text-slate-500">Evaluaciones por período (10/20/30/40 semanas)</p>
              </div>
              <div className="flex gap-3 flex-wrap items-center w-full lg:w-auto">
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                  <SelectTrigger className="w-full sm:w-40" data-testid="period-selector">
                    <SelectValue placeholder="Período" />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIODS.map((p) => (
                      <SelectItem key={p.value} value={p.value} data-testid={`period-option-${p.value}`}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Seleccionar Materia" />
                  </SelectTrigger>
                  <SelectContent>
                    {teacherProfile?.subjects && teacherProfile.subjects.length > 0 ? (
                      teacherProfile.subjects.filter(subject => subject && subject.trim()).map((subject) => (
                        <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-subjects">No hay materias asignadas</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <Dialog open={showGradeDialog} onOpenChange={setShowGradeDialog}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary w-full sm:w-auto">
                      <Plus className="w-4 h-4 mr-2" />
                      Nueva Calificación
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Registrar Calificación</DialogTitle>
                      <DialogDescription>
                        Completa la información de la nueva calificación.
                      </DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handleGradeSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="grade_student">Estudiante *</Label>
                        <Select
                          value={gradeForm.student_id}
                          onValueChange={(value) => setGradeForm({...gradeForm, student_id: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar estudiante" />
                          </SelectTrigger>
                          <SelectContent>
                            {students.map((student) => (
                              <SelectItem key={student.id} value={student.id}>
                                {student.first_name} {student.last_name_paternal}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="grade_subject">Materia *</Label>
                        <Select
                          value={gradeForm.subject}
                          onValueChange={(value) => setGradeForm({...gradeForm, subject: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar materia" />
                          </SelectTrigger>
                          <SelectContent>
                            {teacherProfile?.subjects && teacherProfile.subjects.length > 0 ? (
                              teacherProfile.subjects.filter(subject => subject && subject.trim()).map((subject) => (
                                <SelectItem key={subject} value={subject}>
                                  {subject}
                                </SelectItem>
                              ))
                            ) : (
                              <SelectItem value="no-subjects">No hay materias</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="description">Descripción de la Evaluación *</Label>
                        <Input
                          id="description"
                          value={gradeForm.description || ''}
                          onChange={(e) => setGradeForm({...gradeForm, description: e.target.value})}
                          placeholder="Ej: Evaluación 1, Examen Final..."
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="evaluation_date">Fecha de la Evaluación</Label>
                        <Input
                          id="evaluation_date"
                          type="date"
                          value={gradeForm.evaluation_date || ''}
                          onChange={(e) => setGradeForm({...gradeForm, evaluation_date: e.target.value})}
                          data-testid="evaluation-date-input"
                        />
                      </div>

                      <div>
                        <Label htmlFor="grade_system">Sistema de Calificación</Label>
                        <div className="p-2 bg-gray-100 rounded-md text-sm">
                          <span className="font-medium">
                            {gradingSystem === 'points' && 'Puntos (0-4)'}
                            {gradingSystem === 'percentage' && 'Porcentaje (0-100%)'}
                            {gradingSystem === 'letter' && 'Letras (A-F)'}
                            {gradingSystem === 'custom_points' && 'Puntos Personalizados (X/Y)'}
                          </span>
                          <p className="text-xs text-slate-500 mt-1">
                            Configurado por el administrador
                          </p>
                        </div>
                      </div>

                      {gradingSystem === 'custom_points' ? (
                        <>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label htmlFor="grade_value">Puntos Obtenidos *</Label>
                              <Input
                                id="grade_value"
                                type="number"
                                step="0.01"
                                min="0"
                                value={gradeForm.grade_value}
                                onChange={(e) => setGradeForm({...gradeForm, grade_value: e.target.value})}
                                placeholder="ej: 19"
                                required
                                data-testid="grade-value-input"
                              />
                            </div>
                            <div>
                              <Label htmlFor="max_points">Puntos Máximos *</Label>
                              <Input
                                id="max_points"
                                type="number"
                                step="0.01"
                                min="1"
                                value={gradeForm.max_points}
                                onChange={(e) => setGradeForm({...gradeForm, max_points: e.target.value})}
                                placeholder="ej: 20"
                                required
                                data-testid="grade-max-points-input"
                              />
                            </div>
                          </div>
                          {gradeForm.grade_value && gradeForm.max_points && parseFloat(gradeForm.max_points) > 0 && (
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="grade-preview">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-blue-900">
                                  {gradeForm.grade_value}/{gradeForm.max_points} = {((parseFloat(gradeForm.grade_value) / parseFloat(gradeForm.max_points)) * 100).toFixed(1)}%
                                </span>
                                <span className={`text-lg font-bold ${
                                  ((parseFloat(gradeForm.grade_value) / parseFloat(gradeForm.max_points)) * 100) >= 90 ? 'text-green-600' :
                                  ((parseFloat(gradeForm.grade_value) / parseFloat(gradeForm.max_points)) * 100) >= 80 ? 'text-blue-600' :
                                  ((parseFloat(gradeForm.grade_value) / parseFloat(gradeForm.max_points)) * 100) >= 70 ? 'text-yellow-600' :
                                  ((parseFloat(gradeForm.grade_value) / parseFloat(gradeForm.max_points)) * 100) >= 60 ? 'text-orange-600' :
                                  'text-red-600'
                                }`}>
                                  {((parseFloat(gradeForm.grade_value) / parseFloat(gradeForm.max_points)) * 100) >= 90 ? 'A' :
                                   ((parseFloat(gradeForm.grade_value) / parseFloat(gradeForm.max_points)) * 100) >= 80 ? 'B' :
                                   ((parseFloat(gradeForm.grade_value) / parseFloat(gradeForm.max_points)) * 100) >= 70 ? 'C' :
                                   ((parseFloat(gradeForm.grade_value) / parseFloat(gradeForm.max_points)) * 100) >= 60 ? 'D' : 'F'}
                                </span>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div>
                            <Label htmlFor="grade_value">
                              Calificación *
                              {gradingSystem === 'points' && ' (0-4)'}
                              {gradingSystem === 'percentage' && ' (0-100%)'}
                              {gradingSystem === 'letter' && ' (A, B, C, D, F)'}
                            </Label>
                            <Input
                              id="grade_value"
                              type="number"
                              step="0.01"
                              value={gradeForm.grade_value}
                              onChange={(e) => setGradeForm({...gradeForm, grade_value: e.target.value})}
                              required
                              data-testid="grade-value-input"
                            />
                          </div>

                          {gradeForm.grade_system === 'points' && (
                            <div>
                              <Label htmlFor="max_points">Puntos Máximos</Label>
                              <Input
                                id="max_points"
                                type="number"
                                step="0.01"
                                value={gradeForm.max_points}
                                onChange={(e) => setGradeForm({...gradeForm, max_points: e.target.value})}
                                data-testid="grade-max-points-input"
                              />
                            </div>
                          )}
                        </>
                      )}

                      <div>
                        <Label htmlFor="grade_notes">Anotaciones del Profesor</Label>
                        <Textarea
                          id="grade_notes"
                          value={gradeForm.notes}
                          onChange={(e) => setGradeForm({...gradeForm, notes: e.target.value})}
                          placeholder="Observaciones o comentarios sobre el desempeño del estudiante (visible para los padres)"
                          data-testid="grade-notes-input"
                        />
                      </div>

                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setShowGradeDialog(false)}>
                          Cancelar
                        </Button>
                        {gradeForm.grade_id && (
                          <Button
                            type="button"
                            variant="outline"
                            className="border-red-200 text-red-600 hover:bg-red-50"
                            onClick={handleDeleteGrade}
                            disabled={loading}
                            data-testid="delete-grade-btn"
                          >
                            <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                          </Button>
                        )}
                        <Button type="submit" disabled={loading}>
                          {loading ? 'Registrando...' : 'Registrar Calificación'}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {selectedSubject && (
              <>
                {/* Vista móvil de calificaciones (tarjetas) */}
                <div className="md:hidden space-y-4" data-testid="grades-mobile-list">
                  {students.length === 0 ? (
                    <Card className="modern-card"><CardContent className="text-center py-8 text-slate-400">No hay estudiantes en este grado</CardContent></Card>
                  ) : (
                    students.map((student) => {
                      const allStudentGrades = grades.filter(g => g.student_id === student.id);
                      const studentGrades = allStudentGrades.filter(g => (g.period || '10') === selectedPeriod);
                      const gradeSlots = Array(evaluationDescriptions.length).fill(null);
                      studentGrades.forEach(grade => {
                        const descIndex = evaluationDescriptions.findIndex(desc => desc === grade.description);
                        if (descIndex >= 0) gradeSlots[descIndex] = grade;
                        else if (grade.description && grade.description.startsWith('Evaluación')) {
                          const evalNum = parseInt(grade.description.split(' ')[1]);
                          if (evalNum >= 1 && evalNum <= evaluationDescriptions.length) gradeSlots[evalNum - 1] = grade;
                        }
                      });
                      // NOTE: average must come from studentGrades (all grades saved for this
                      // period), NOT from gradeSlots — a grade whose description doesn't match
                      // any current slot (custom text, or a duplicate description overwriting
                      // another slot) would otherwise be silently dropped from the average while
                      // still counting in finalSummary, causing period vs. final to mismatch.
                      const periodSummary = computeGradeSummary(studentGrades);
                      const finalSummary = computeGradeSummary(allStudentGrades);
                      return (
                        <Card key={student.id} className="relative overflow-hidden bg-white rounded-2xl border border-slate-100 shadow-[0_2px_12px_-4px_rgba(15,23,42,0.08)]" data-testid={`grade-card-${student.id}`}>
                          <div className="absolute left-0 top-0 bottom-0 w-1 bg-orange-500"></div>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <div className="min-w-0">
                                <p className="font-semibold text-slate-900 break-words">{student.last_name_paternal} {student.last_name_maternal}, {student.first_name} {student.middle_name || ''}</p>
                                <p className="text-xs text-slate-500 mt-0.5">Prom {selectedPeriod}: <span className="font-semibold text-slate-700">{periodSummary.average}</span> · Final: <span className="font-semibold text-slate-700">{finalSummary.average}</span></p>
                              </div>
                              <div className="shrink-0 flex flex-col items-center bg-orange-50 border border-orange-200 text-orange-700 px-3 py-1.5 rounded-xl shadow-sm">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-orange-500/80">Nota</span>
                                <span className="text-xl font-bold leading-none">{periodSummary.displayGrade || '-'}</span>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {gradeSlots.map((gradeData, i) => {
                                const existingGrade = studentGrades.find(g => g.description === evaluationDescriptions[i]);
                                let cellDisplay = '';
                                if (gradeData && gradeData.grade_value !== null && gradeData.grade_value !== undefined) {
                                  cellDisplay = gradingSystem === 'custom_points' ? `${gradeData.grade_value}/${gradeData.max_points || '?'}` : `${gradeData.grade_value}`;
                                }
                                return (
                                  <button
                                    key={i}
                                    type="button"
                                    data-testid={`grade-chip-${student.id}-${i}`}
                                    onClick={() => {
                                      setGradeForm({
                                        student_id: student.id,
                                        subject: selectedSubject,
                                        grade_value: existingGrade ? existingGrade.grade_value : '',
                                        grade_system: gradingSystem,
                                        max_points: existingGrade ? (existingGrade.max_points || '') : '',
                                        description: evaluationDescriptions[i],
                                        notes: existingGrade ? (existingGrade.notes || '') : '',
                                        period: selectedPeriod,
                                        evaluation_date: existingGrade?.evaluation_date || evaluationDates[i] || '',
                                        grade_id: existingGrade ? existingGrade.id : null
                                      });
                                      setShowGradeDialog(true);
                                    }}
                                    className={`relative flex flex-col items-center rounded-lg border py-1.5 px-2.5 min-w-[52px] shadow-sm transition-transform active:scale-95 ${cellDisplay !== '' ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-white border-slate-200 text-slate-400 hover:border-blue-300 hover:shadow-md'}`}
                                  >
                                    <span className="text-[10px] font-bold text-slate-400">#{i + 1}</span>
                                    <span className="text-sm font-bold min-h-[18px]">{cellDisplay || '—'}</span>
                                    {existingGrade?.notes && <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-blue-500 rounded-full"></span>}
                                    {existingGrade?.views?.length > 0 && (
                                      <span
                                        className="absolute bottom-1 left-1 w-1.5 h-1.5 bg-emerald-500 rounded-full"
                                        title={`Visto por: ${existingGrade.views.map(v => v.parent_name).join(', ')}`}
                                        data-testid={`grade-seen-indicator-${student.id}-${i}`}
                                      ></span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </div>

                {/* Tabla de Calificaciones (escritorio) */}
                <Card className="modern-card hidden md:block">
                  <CardContent className="p-6">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="border-b-2 border-gray-300">
                            <th className="px-2 py-2 text-left bg-gray-100 font-semibold border-r">Apellidos</th>
                            <th className="px-2 py-2 text-left bg-gray-100 font-semibold border-r">Nombre</th>
                            {evaluationDescriptions.map((desc, i) => (
                              <th key={i} className={`px-2 py-2 text-center bg-gray-100 font-semibold border-r ${gradingSystem === 'custom_points' ? 'min-w-[60px]' : 'min-w-[40px]'}`} title={desc}>
                                <div>{i + 1}</div>
                                {evaluationDates[i] && (
                                  <div className="text-[10px] font-normal text-slate-500">{formatLocalDate(evaluationDates[i])}</div>
                                )}
                              </th>
                            ))}
                            <th className="px-2 py-2 text-center bg-blue-100 font-semibold border-r" title="Promedio del período seleccionado">Prom {selectedPeriod}</th>
                            <th className="px-2 py-2 text-center bg-blue-100 font-semibold border-r">Nota</th>
                            <th className="px-2 py-2 text-center bg-emerald-100 font-semibold border-r" title="Nota final global (todos los períodos)">Final</th>
                            <th className="px-2 py-2 text-center bg-emerald-100 font-semibold">Nota Final</th>
                          </tr>
                        </thead>
                        <tbody>
                          {students.length === 0 ? (
                            <tr>
                              <td colSpan={evaluationDescriptions.length + 6} className="text-center py-8 text-slate-400">
                                No hay estudiantes en este grado
                              </td>
                            </tr>
                          ) : (
                            students.map((student) => {
                              // All grades for this student in this subject (all periods)
                              const allStudentGrades = grades.filter(g => g.student_id === student.id);
                              // Grades for the currently selected period
                              const studentGrades = allStudentGrades.filter(g => (g.period || '10') === selectedPeriod);

                              // Build slots dynamically based on evaluation descriptions
                              const gradeSlots = Array(evaluationDescriptions.length).fill(null);
                              studentGrades.forEach(grade => {
                                const descIndex = evaluationDescriptions.findIndex(desc => desc === grade.description);
                                if (descIndex >= 0) {
                                  gradeSlots[descIndex] = grade;
                                } else if (grade.description && grade.description.startsWith('Evaluación')) {
                                  const evalNum = parseInt(grade.description.split(' ')[1]);
                                  if (evalNum >= 1 && evalNum <= evaluationDescriptions.length) {
                                    gradeSlots[evalNum - 1] = grade;
                                  }
                                }
                              });

                              // Period summary: must use studentGrades (ALL grades saved for this
                              // period), not gradeSlots — slot-matching by description text can
                              // drop grades with custom/duplicate descriptions, which would then
                              // undercount vs. finalSummary (unfiltered) and cause a mismatch.
                              const periodSummary = computeGradeSummary(studentGrades);
                              // Global final summary across ALL periods
                              const finalSummary = computeGradeSummary(allStudentGrades);
                              const average = periodSummary.average;
                              const displayGrade = periodSummary.displayGrade;

                              return (
                                <tr key={student.id} className="border-b hover:bg-gray-50">
                                  <td className="px-2 py-2 border-r font-medium">
                                    {student.last_name_paternal} {student.last_name_maternal}
                                  </td>
                                  <td className="px-2 py-2 border-r">
                                    {student.first_name} {student.middle_name || ''}
                                  </td>
                                  {gradeSlots.map((gradeData, i) => {
                                    const existingGrade = studentGrades.find(g =>
                                      g.description === evaluationDescriptions[i]
                                    );

                                    let cellDisplay = '';
                                    let cellTitle = existingGrade?.notes || '';

                                    if (gradeData && gradeData.grade_value !== null && gradeData.grade_value !== undefined) {
                                      if (gradingSystem === 'custom_points') {
                                        const pct = gradeData.max_points && gradeData.max_points > 0
                                          ? ((gradeData.grade_value / gradeData.max_points) * 100).toFixed(0)
                                          : '?';
                                        cellDisplay = `${gradeData.grade_value}/${gradeData.max_points || '?'}`;
                                        cellTitle = `${gradeData.grade_value}/${gradeData.max_points || '?'} = ${pct}%${existingGrade?.notes ? ' | ' + existingGrade.notes : ''}`;
                                      } else {
                                        cellDisplay = gradeData.grade_value;
                                      }
                                    }

                                    return (
                                      <td
                                        key={i}
                                        className={`px-2 py-2 text-center border-r cursor-pointer hover:bg-blue-50 relative ${gradingSystem === 'custom_points' ? 'text-xs' : ''}`}
                                        title={cellTitle}
                                        onClick={() => {
                                          setGradeForm({
                                            student_id: student.id,
                                            subject: selectedSubject,
                                            grade_value: existingGrade ? existingGrade.grade_value : '',
                                            grade_system: gradingSystem,
                                            max_points: existingGrade ? (existingGrade.max_points || '') : '',
                                            description: evaluationDescriptions[i],
                                            notes: existingGrade ? (existingGrade.notes || '') : '',
                                            period: selectedPeriod,
                                            evaluation_date: existingGrade?.evaluation_date || evaluationDates[i] || '',
                                            grade_id: existingGrade ? existingGrade.id : null
                                          });
                                          setShowGradeDialog(true);
                                        }}
                                      >
                                        {cellDisplay}
                                        {existingGrade?.notes && (
                                          <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-blue-500 rounded-full" title="Tiene anotaciones"></span>
                                        )}
                                        {existingGrade?.views?.length > 0 && (
                                          <span
                                            className="absolute bottom-0.5 left-0.5 w-2 h-2 bg-emerald-500 rounded-full"
                                            title={`Visto por: ${existingGrade.views.map(v => v.parent_name).join(', ')}`}
                                            data-testid={`grade-seen-indicator-desktop-${student.id}-${i}`}
                                          ></span>
                                        )}
                                      </td>
                                    );
                                  })}
                                  <td className="px-2 py-2 text-center border-r font-semibold bg-blue-50">
                                    {average}
                                  </td>
                                  <td className={`px-2 py-2 text-center border-r font-bold text-lg ${
                                    displayGrade === 'A' ? 'text-green-600' :
                                    displayGrade === 'B' ? 'text-blue-600' :
                                    displayGrade === 'C' ? 'text-yellow-600' :
                                    displayGrade === 'D' ? 'text-orange-600' :
                                    displayGrade === 'F' ? 'text-red-600' : ''
                                  }`}>
                                    {displayGrade}
                                  </td>
                                  <td className="px-2 py-2 text-center border-r font-semibold bg-emerald-50" title="Global de todos los períodos">
                                    {finalSummary.average}
                                  </td>
                                  <td className={`px-2 py-2 text-center font-bold text-lg bg-emerald-50 ${
                                    finalSummary.displayGrade === 'A' ? 'text-green-600' :
                                    finalSummary.displayGrade === 'B' ? 'text-blue-600' :
                                    finalSummary.displayGrade === 'C' ? 'text-yellow-600' :
                                    finalSummary.displayGrade === 'D' ? 'text-orange-600' :
                                    finalSummary.displayGrade === 'F' ? 'text-red-600' : ''
                                  }`}>
                                    {finalSummary.displayGrade}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Área de Descripciones */}
                <Card className="modern-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-blue-600" />
                      Descripciones de Evaluaciones — {PERIODS.find(p => p.value === selectedPeriod)?.label}
                    </CardTitle>
                    <CardDescription>
                      Define qué representa cada columna. Puedes agregar más de 15 evaluaciones. Estas descripciones aplican al período seleccionado.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {evaluationDescriptions.map((desc, i) => (
                        <div key={i} className="p-3 border rounded-lg hover:border-blue-500 transition-colors">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-blue-600">#{i + 1}</span>
                            {evaluationDescriptions.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeEvaluationColumn(i)}
                                className="text-red-400 hover:text-red-600"
                                title="Quitar esta evaluación"
                                data-testid={`remove-eval-${i}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <Input
                            placeholder="Ej: Examen, Tarea, Proyecto..."
                            className="text-xs"
                            value={desc}
                            onChange={(e) => {
                              const newDescriptions = [...evaluationDescriptions];
                              newDescriptions[i] = e.target.value;
                              setEvaluationDescriptions(newDescriptions);
                            }}
                          />
                          <Input
                            type="date"
                            className="text-xs mt-2"
                            value={evaluationDates[i] || ''}
                            onChange={(e) => {
                              const newDates = [...evaluationDates];
                              newDates[i] = e.target.value;
                              setEvaluationDates(newDates);
                            }}
                            data-testid={`eval-date-${i}`}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex flex-wrap justify-between gap-2">
                      <Button
                        variant="outline"
                        className="border-blue-200 text-blue-700 hover:bg-blue-50"
                        onClick={addEvaluationColumn}
                        data-testid="add-eval-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Agregar evaluación
                      </Button>
                      <Button 
                        className="bg-blue-600 hover:bg-blue-700"
                        disabled={loading}
                        data-testid="save-descriptions-btn"
                        onClick={handleSaveDescriptions}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {loading ? 'Guardando...' : 'Guardar Descripciones'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Attendance Tab */}
        {activeTab === 'attendance' && (
          <div className="space-y-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-center">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Asistencia</h2>
                <p className="text-slate-500">Registra la asistencia diaria de tus estudiantes</p>
              </div>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 w-full lg:w-auto">
                <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Seleccionar Materia" />
                  </SelectTrigger>
                  <SelectContent>
                    {teacherProfile?.subjects && teacherProfile.subjects.length > 0 ? (
                      teacherProfile.subjects.filter(subject => subject && subject.trim()).map((subject) => (
                        <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-subjects">No hay materias asignadas</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full sm:w-40"
                />
                <Dialog open={showAttendanceDialog} onOpenChange={setShowAttendanceDialog}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary w-full sm:w-auto">
                      <Plus className="w-4 h-4 mr-2" />
                      Registrar Asistencia
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Registrar Asistencia</DialogTitle>
                      <DialogDescription>
                        Registra la asistencia de un estudiante.
                      </DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handleAttendanceSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="attendance_student">Estudiante *</Label>
                        <Select
                          value={attendanceForm.student_id}
                          onValueChange={(value) => setAttendanceForm({...attendanceForm, student_id: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar estudiante" />
                          </SelectTrigger>
                          <SelectContent>
                            {students.map((student) => (
                              <SelectItem key={student.id} value={student.id}>
                                {student.first_name} {student.last_name_paternal}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="attendance_date">Fecha *</Label>
                        <Input
                          id="attendance_date"
                          type="date"
                          value={attendanceForm.date}
                          onChange={(e) => setAttendanceForm({...attendanceForm, date: e.target.value})}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="attendance_status">Estado *</Label>
                        <Select
                          value={attendanceForm.status}
                          onValueChange={(value) => setAttendanceForm({...attendanceForm, status: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="present">Presente</SelectItem>
                            <SelectItem value="absent">Ausente</SelectItem>
                            <SelectItem value="late">Tarde</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="attendance_notes">Notas</Label>
                        <Textarea
                          id="attendance_notes"
                          value={attendanceForm.notes}
                          onChange={(e) => setAttendanceForm({...attendanceForm, notes: e.target.value})}
                          placeholder="Notas adicionales (opcional)"
                        />
                      </div>

                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setShowAttendanceDialog(false)}>
                          Cancelar
                        </Button>
                        <Button type="submit" disabled={loading}>
                          {loading ? 'Registrando...' : 'Registrar Asistencia'}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {selectedSubject && selectedDate && (
              <Card className="modern-card">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <CardTitle>Asistencia de {selectedSubject} - {formatLocalDate(selectedDate)}</CardTitle>
                      <CardDescription>
                        Selecciona el estado de cada estudiante y guarda todo de una vez
                      </CardDescription>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-slate-400 mr-1">Marcar todos:</span>
                      <Button size="sm" variant="outline" className="text-green-600 border-green-200 hover:bg-green-50" onClick={() => setAllAttendance('present')} data-testid="mark-all-present-btn">Presente</Button>
                      <Button size="sm" variant="outline" className="text-yellow-600 border-yellow-200 hover:bg-yellow-50" onClick={() => setAllAttendance('late')} data-testid="mark-all-late-btn">Tarde</Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setAllAttendance('absent')} data-testid="mark-all-absent-btn">Ausente</Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {students.length === 0 ? (
                    <p className="text-slate-400 text-center py-8">No hay estudiantes para esta materia</p>
                  ) : (
                    <>
                      {/* Vista móvil de asistencia (tarjetas) */}
                      <div className="md:hidden p-3 space-y-3" data-testid="attendance-mobile-list">
                        {students.map((student) => {
                          const current = bulkAttendance[student.id] || 'present';
                          const initials = `${(student.first_name || ' ')[0]}${(student.last_name_paternal || ' ')[0]}`.toUpperCase();
                          const options = [
                            { key: 'present', label: 'Presente', cls: 'bg-emerald-50 border-emerald-500 text-emerald-700 ring-emerald-500' },
                            { key: 'late', label: 'Tarde', cls: 'bg-amber-50 border-amber-500 text-amber-700 ring-amber-500' },
                            { key: 'absent', label: 'Ausente', cls: 'bg-rose-50 border-rose-500 text-rose-700 ring-rose-500' },
                          ];
                          return (
                            <div key={student.id} data-testid={`attendance-card-${student.id}`} className="relative overflow-hidden bg-white p-4 rounded-2xl border border-slate-100 shadow-[0_2px_12px_-4px_rgba(15,23,42,0.08)]">
                              <div className="absolute left-0 top-0 bottom-0 w-1 bg-orange-500"></div>
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-full bg-slate-50 border border-slate-100 text-slate-600 flex items-center justify-center font-bold text-sm shrink-0">{initials}</div>
                                <p className="font-semibold text-slate-900">{student.first_name} {student.last_name_paternal}</p>
                              </div>
                              <div className="grid grid-cols-3 gap-2">
                                {options.map((opt) => {
                                  const activeSel = current === opt.key;
                                  return (
                                    <button
                                      key={opt.key}
                                      type="button"
                                      aria-pressed={activeSel}
                                      onClick={() => setBulkAttendance({ ...bulkAttendance, [student.id]: opt.key })}
                                      data-testid={`attendance-m-${opt.key}-${student.id}`}
                                      className={`flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl border text-sm transition-transform active:scale-95 ${activeSel ? `${opt.cls} font-bold ring-1 shadow-sm` : 'border-slate-200 bg-slate-50 text-slate-500 font-medium hover:bg-slate-100'}`}
                                    >
                                      {activeSel && <CheckCircle className="w-4 h-4" />}
                                      {opt.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="overflow-x-auto hidden md:block">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Estudiante</th>
                              <th className="text-center">Presente</th>
                              <th className="text-center">Tarde</th>
                              <th className="text-center">Ausente</th>
                            </tr>
                          </thead>
                          <tbody>
                            {students.map((student) => {
                              const current = bulkAttendance[student.id] || 'present';
                              const options = [
                                { key: 'present', color: 'bg-green-600', ring: 'ring-green-500' },
                                { key: 'late', color: 'bg-yellow-500', ring: 'ring-yellow-400' },
                                { key: 'absent', color: 'bg-red-600', ring: 'ring-red-500' },
                              ];
                              return (
                                <tr key={student.id} data-testid={`attendance-row-${student.id}`}>
                                  <td>
                                    <p className="font-medium text-slate-800">
                                      {student.first_name} {student.last_name_paternal}
                                    </p>
                                  </td>
                                  {options.map((opt) => (
                                    <td key={opt.key} className="text-center">
                                      <button
                                        type="button"
                                        onClick={() => setBulkAttendance({ ...bulkAttendance, [student.id]: opt.key })}
                                        data-testid={`attendance-${opt.key}-${student.id}`}
                                        className={`w-6 h-6 rounded-full border-2 transition-all mx-auto flex items-center justify-center ${
                                          current === opt.key
                                            ? `${opt.color} border-transparent ring-2 ${opt.ring} ring-offset-1`
                                            : 'bg-white border-slate-300 hover:border-slate-400'
                                        }`}
                                        aria-label={opt.key}
                                      >
                                        {current === opt.key && <CheckCircle className="w-4 h-4 text-white" />}
                                      </button>
                                    </td>
                                  ))}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex justify-end p-4 border-t">
                        <Button onClick={handleBulkAttendanceSave} disabled={savingBulk} className="btn-primary w-full sm:w-auto" data-testid="save-bulk-attendance-btn">
                          <Save className="w-4 h-4 mr-2" />
                          {savingBulk ? 'Guardando...' : 'Guardar toda la asistencia'}
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'clockinout' && <ClockInOutTab />}
        {activeTab === 'calendars' && <CalendarsTab />}
        {activeTab === 'myprofile' && <MyProfileTab />}
        {activeTab === 'planning' && <WeeklyPlanningTab role="teacher" />}
        {activeTab === 'leave-requests' && <LeaveRequestsTab role="teacher" />}
      </div>

      {/* Student Info Modal */}
      <Dialog open={!!studentInfo} onOpenChange={(open) => { if (!open) { setStudentInfo(null); setStudentAttendance(null); } }}>
        <DialogContent className="max-w-md" data-testid="student-info-dialog">
          <DialogHeader>
            <DialogTitle>
              {studentInfo?.first_name} {studentInfo?.middle_name || ''} {studentInfo?.last_name_paternal} {studentInfo?.last_name_maternal || ''}
            </DialogTitle>
            <DialogDescription>Información del estudiante</DialogDescription>
          </DialogHeader>
          {studentInfo && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-400">Número:</span> {studentInfo.student_number || '—'}</div>
                <div><span className="text-slate-400">Grado:</span> {studentInfo.assigned_grade || '—'}</div>
                <div><span className="text-slate-400">Email:</span> {studentInfo.email || '—'}</div>
                <div><span className="text-slate-400">Teléfono:</span> {studentInfo.phone_number || '—'}</div>
                <div className="col-span-2"><span className="text-slate-400">Dirección:</span> {studentInfo.residential_address || '—'}</div>
              </div>

              <div className="pt-2 border-t">
                <p className="font-semibold text-slate-700 mb-2">Asistencia</p>
                {studentAttendance ? (
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-green-50 rounded-lg p-2" data-testid="att-present">
                      <p className="text-xl font-bold text-green-600">{studentAttendance.present}</p>
                      <p className="text-[11px] text-slate-500">Presente</p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-2" data-testid="att-late">
                      <p className="text-xl font-bold text-yellow-600">{studentAttendance.late}</p>
                      <p className="text-[11px] text-slate-500">Tardanzas</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-2" data-testid="att-absent">
                      <p className="text-xl font-bold text-red-600">{studentAttendance.absent}</p>
                      <p className="text-[11px] text-slate-500">Ausencias</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-400 text-xs">Cargando asistencia...</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeacherDashboard;